import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web'

import type { LogEntry, LogTransport } from '../types'
import { LogLevel } from '../types'

/**
 * Application Insights transport - sends logs to Azure Application Insights
 * Used in QAL, PPD, and PRD environments
 */
export class AppInsightsTransport implements LogTransport {
  private minLevel: LogLevel
  private appInsights: ApplicationInsights | null = null

  constructor(minLevel: LogLevel = LogLevel.WARN) {
    this.minLevel = minLevel
  }

  /**
   * Initialize Application Insights instance
   * Must be called after AppInsights is loaded in the app
   */
  setAppInsights(appInsights: ApplicationInsights): void {
    this.appInsights = appInsights
  }

  shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel && this.appInsights !== null
  }

  log(entry: LogEntry): void {
    if (!this.shouldLog(entry.level) || !this.appInsights) {
      return
    }

    const { level, message, context, error, environment, userId } = entry

    // Prepare properties for Application Insights
    const properties = {
      ...context,
      environment,
      userId,
      userAgent: entry.userAgent,
      timestamp: entry.timestamp,
    }

    // Remove undefined values
    Object.keys(properties).forEach((key) => {
      if (properties[key as keyof typeof properties] === undefined) {
        delete properties[key as keyof typeof properties]
      }
    })

    try {
      // Send error/critical as exceptions
      if ((level === LogLevel.ERROR || level === LogLevel.CRITICAL) && error) {
        this.appInsights.trackException({
          exception: error,
          severityLevel: this.mapLogLevelToSeverity(level),
          properties: {
            ...properties,
            message,
          },
        })
      } else {
        // Send debug/info/warn as traces
        this.appInsights.trackTrace({
          message,
          severityLevel: this.mapLogLevelToSeverity(level),
          properties,
        })
      }
    } catch (err) {
      // Fallback to console if AppInsights fails
      console.error('Failed to send log to Application Insights:', err)
      console.error('Original log:', { level, message, context, error })
    }
  }

  /**
   * Map internal log level to Application Insights severity level
   */
  private mapLogLevelToSeverity(level: LogLevel): SeverityLevel {
    switch (level) {
      case LogLevel.DEBUG:
        return SeverityLevel.Verbose
      case LogLevel.INFO:
        return SeverityLevel.Information
      case LogLevel.WARN:
        return SeverityLevel.Warning
      case LogLevel.ERROR:
        return SeverityLevel.Error
      case LogLevel.CRITICAL:
        return SeverityLevel.Critical
      default:
        return SeverityLevel.Information
    }
  }
}
